import {
  useState,
  useRef,
  useImperativeHandle,
  forwardRef,
  useEffect,
} from "react";

// Helper function to detect supported MIME type
const getSupportedMimeType = () => {
  const types = [
    "audio/mp4",   // iOS Safari
    "audio/aac",   // fallback iOS
    "audio/webm",  // Chrome / Firefox
  ];

  return types.find((type) =>
    window.MediaRecorder?.isTypeSupported(type)
  );
};

const AudioRecorder = forwardRef(
  ({ onRecordingComplete, onRecordingError }, ref) => {
    const [isRecording, setIsRecording] = useState(false);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const streamRef = useRef(null);
    const completionCalledRef = useRef(false);
    const mimeTypeRef = useRef(null);

    useImperativeHandle(ref, () => ({
      isRecording,
      startRecording: async () => {
        if (isRecording) return false;
        try {
          // Reset completion flag for new recording
          completionCalledRef.current = false;

          // Detect supported MIME type
          const mimeType = getSupportedMimeType();
          if (!mimeType) {
            const error = new Error("No supported audio MIME type found for this browser");
            console.error(error.message);
            onRecordingError?.(error);
            return false;
          }
          mimeTypeRef.current = mimeType;

          const stream = await navigator.mediaDevices.getUserMedia({
            audio: true,
            video: false,
          });
          streamRef.current = stream;
          mediaRecorderRef.current = new MediaRecorder(stream, {
            mimeType: mimeType,
          });
          audioChunksRef.current = [];

          mediaRecorderRef.current.ondataavailable = (event) => {
            if (event.data.size > 0) {
              audioChunksRef.current.push(event.data);
            }
          };

          mediaRecorderRef.current.onstop = () => {
            console.log("MediaRecorder onstop event triggered");
            const audioBlob = new Blob(audioChunksRef.current, {
              type: mimeTypeRef.current || "audio/webm",
            });
            if (
              audioBlob.size > 0 &&
              onRecordingComplete &&
              !completionCalledRef.current
            ) {
              console.log("Calling onRecordingComplete from onstop event");
              onRecordingComplete(audioBlob);
              completionCalledRef.current = true;
            }
          };

          mediaRecorderRef.current.onerror = (event) => {
            console.error("MediaRecorder error:", event.error);
            onRecordingError?.(event.error);
            setIsRecording(false);
            streamRef.current?.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
          };

          mediaRecorderRef.current.start();
          setIsRecording(true);
          return true;
        } catch (error) {
          console.error("Error accessing microphone:", error);
          onRecordingError?.(error);
          setIsRecording(false);
          return false;
        }
      },

      stopRecording: () => {
        console.log("stopRecording called", {
          isRecording,
          mediaRecorderState: mediaRecorderRef.current?.state,
        });

        if (
          !isRecording ||
          !mediaRecorderRef.current ||
          mediaRecorderRef.current.state !== "recording"
        ) {
          console.log("stopRecording: Manual completion path");
          // If not recording or recorder is not in recording state,
          // clean up and call completion manually
          if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
          }
          const audioBlob = audioChunksRef.current.length
            ? new Blob(audioChunksRef.current, { 
                type: mimeTypeRef.current || "audio/webm" 
              })
            : null;

          if (!completionCalledRef.current) {
            console.log("Calling onRecordingComplete from manual path");
            onRecordingComplete(audioBlob);
            completionCalledRef.current = true;
          }
          setIsRecording(false);
          return;
        }

        console.log("stopRecording: MediaRecorder.stop() path");
        // If properly recording, just stop - the onstop event will handle completion
        setIsRecording(false);
        mediaRecorderRef.current.stop();
      },
    }));

    useEffect(() => {
      return () => {
        mediaRecorderRef.current?.state === "recording" &&
          mediaRecorderRef.current.stop();
        streamRef.current?.getTracks().forEach((track) => track.stop());
      };
    }, []);

    return null;
  }
);

export default AudioRecorder;
